import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { RapidStorSettings } from '@/core/utils/rapidstor';

import type { DetailsModalState } from '@/store/bookingSlice';

export interface ModalCtas {
    showPrices: boolean;
    moveIn: boolean;
    reservation: boolean;
    saveQuote: boolean;
}

/**
 * Which action buttons the details modal offers for a location.
 *
 * A `showPrices` modal only ever offers the show-prices button; every other
 * modal type offers the move-in / reservation / save-quote group.
 *
 * Visibility depends on the `allow*` settings alone. It must never depend on
 * `reservationNumberOfDaysInAdvanceChargeFee` — that is a fee *threshold*
 * (see `isFreeReservation`), not a switch for whether reservations are
 * offered. A threshold of 0 means every reservation is charged, which is
 * precisely when the button needs to be shown.
 *
 * @param location
 * @param type — the open modal's type, from `DetailsModalState`
 * @returns which buttons to render
 */
export const resolveModalCtas = (
    location: Location,
    type: DetailsModalState['type'],
): ModalCtas => {
    if (type === 'showPrices') {
        return {
            showPrices: true, moveIn: false, reservation: false, saveQuote: false,
        };
    }

    const settings = RapidStorSettings(location);

    return {
        showPrices: false,
        moveIn: settings.showMoveInButton(),
        reservation: settings.showReservationButton(),
        saveQuote: settings.showSaveQuoteButton(),
    };
};

/**
 * Whether a location's settings permit the unit card's "Book online" CTA,
 * which opens the details modal. Callers combine this with unit-level
 * availability.
 *
 * Kept alongside `resolveModalCtas` so the card and the modal it opens cannot
 * drift apart into a dead end — a card that invites the user into a modal
 * offering them nothing.
 *
 * @param location
 * @returns true when the settings allow booking online
 */
export const allowsBookOnlineCta = (location: Location): boolean => {
    const settings = RapidStorSettings(location);

    return settings.showReservationButton() || settings.showMoveInButton();
};
